import { useQuery } from "@tanstack/react-query";
import {
  Download,
  FileJson,
  FileSpreadsheet,
  GitCompare,
  Loader2,
  RefreshCw,
  Save,
  Search as SearchIcon,
  Sparkles,
  X,
} from "lucide-react";

import {
  SPECIES,
  type EvidenceTier,
  type FilterState,
  type SpeciesKey,
} from "@shared/schema";
import { EVIDENCE_TIER_META, EVIDENCE_TIER_ORDER } from "@/lib/evidence";
import { api } from "@/lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Tooltip } from "./ui/tooltip";
import { cn } from "@/lib/utils";

export interface SidebarContext {
  tissue?: string;
  cellType?: string;
  diseaseMode?: string;
  repairState?: string;
}

interface MusclePreset {
  id: string;
  label: string;
  tissue: string;
  cellType?: string;
  diseaseMode?: string;
  repairState?: string;
}

const MUSCLE_PRESETS: MusclePreset[] = [
  {
    id: "ischemic_muscle",
    label: "Ischemic skeletal muscle",
    tissue: "skeletal_muscle",
    diseaseMode: "ischemia_hypoxia",
    repairState: "ischemic",
  },
  {
    id: "regenerating_muscle",
    label: "Regenerating muscle",
    tissue: "skeletal_muscle",
    repairState: "regenerating",
  },
  {
    id: "fibrotic_muscle",
    label: "Fibrotic muscle (dystrophy/chronic)",
    tissue: "skeletal_muscle",
    diseaseMode: "fibrosis_ecm",
    repairState: "fibrotic",
  },
  {
    id: "ecm_niche",
    label: "ECM remodelling niche",
    tissue: "skeletal_muscle",
    cellType: "fap",
    diseaseMode: "fibrosis_ecm",
  },
  {
    id: "inflammatory",
    label: "Inflammatory infiltrate in injured muscle",
    tissue: "skeletal_muscle",
    cellType: "macrophage_muscle",
    diseaseMode: "inflammation_immune",
    repairState: "inflamed",
  },
  {
    id: "myogenic",
    label: "Myogenic lineage context",
    tissue: "skeletal_muscle",
    cellType: "satellite_cell",
    repairState: "regenerating",
  },
  {
    id: "vascular",
    label: "Vascular / hypoxia-associated context",
    tissue: "vasculature",
    cellType: "endothelial_muscle",
    diseaseMode: "ischemia_hypoxia",
  },
];

interface Props {
  sourceGenes: string[];
  species: SpeciesKey;
  taxId: number;
  filters: FilterState;
  onFiltersChange: (next: FilterState) => void;
  context: SidebarContext;
  onContextChange: (next: SidebarContext) => void;
  onNewSearch: () => void;
  onExportCsv: () => void;
  onExportJson: () => void;
  onSavePanel: () => void;
  onCompare: () => void;
  onRefresh: () => void;
  exportBusy?: "csv" | "json" | null;
  refreshBusy?: boolean;
}

export function ResultsSidebar(props: Props) {
  const tissuesQ = useQuery({ queryKey: ["tissues"], queryFn: () => api.listTissues() });
  const cellTypesQ = useQuery({
    queryKey: ["cellTypes", props.context.tissue],
    queryFn: () => api.listCellTypes(props.context.tissue!),
    enabled: !!props.context.tissue,
  });
  const diseaseModesQ = useQuery({
    queryKey: ["disease-modes"],
    queryFn: () => api.listDiseaseModes(),
  });

  const updateFilters = (patch: Partial<FilterState>) =>
    props.onFiltersChange({ ...props.filters, ...patch });

  const toggleTier = (tier: EvidenceTier) => {
    const set = new Set(props.filters.evidenceTiers);
    if (set.has(tier)) set.delete(tier);
    else set.add(tier);
    updateFilters({ evidenceTiers: Array.from(set) });
  };

  const applyPreset = (p: MusclePreset) => {
    props.onContextChange({
      tissue: p.tissue,
      cellType: p.cellType,
      diseaseMode: p.diseaseMode,
      repairState: p.repairState,
    });
  };

  const presetActive = (p: MusclePreset) =>
    props.context.tissue === p.tissue &&
    props.context.cellType === p.cellType &&
    props.context.diseaseMode === p.diseaseMode &&
    props.context.repairState === p.repairState;

  return (
    <aside className="flex h-full w-full flex-col gap-5 overflow-y-auto border-r border-border/80 bg-card/40 p-4 text-sm">
      <div>
        <button
          type="button"
          onClick={props.onNewSearch}
          className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
        >
          ← New search
        </button>
        <div className="mt-2 font-mono text-base font-semibold text-foreground">
          {props.sourceGenes.slice(0, 3).join(", ")}
          {props.sourceGenes.length > 3 && (
            <span className="text-muted-foreground"> +{props.sourceGenes.length - 3}</span>
          )}
        </div>
        <div className="mt-0.5 text-[11px] text-muted-foreground">
          TaxID: {props.taxId} · {SPECIES[props.species].label}
        </div>
      </div>

      <section className="space-y-2">
        <div className="label-eyebrow flex items-center gap-1">
          <Sparkles className="h-3 w-3 text-accent" />
          Biological Context
        </div>
        <div className="text-[11px] text-muted-foreground">Muscle presets ⓘ</div>
        <div className="flex flex-col gap-1.5">
          {MUSCLE_PRESETS.map((p) => (
            <button
              key={p.id}
              type="button"
              onClick={() => applyPreset(p)}
              className={cn(
                "truncate rounded-md border px-2.5 py-1 text-left text-xs transition-colors",
                presetActive(p)
                  ? "border-accent/60 bg-accent/10 text-foreground"
                  : "border-border bg-card hover:bg-muted",
              )}
              title={p.label}
            >
              {p.label}
            </button>
          ))}
        </div>
      </section>

      <section className="space-y-1.5">
        <Label>Tissue</Label>
        <select
          value={props.context.tissue ?? ""}
          onChange={(e) =>
            props.onContextChange({
              ...props.context,
              tissue: e.currentTarget.value || undefined,
              cellType: undefined,
            })
          }
          className="flex h-9 w-full rounded-md border border-input bg-background px-2 text-sm"
        >
          <option value="">—</option>
          {tissuesQ.data?.map((t) => (
            <option key={t.id} value={t.id}>
              {t.label}
            </option>
          ))}
        </select>
      </section>

      <section className="space-y-1.5">
        <Label>Cell type</Label>
        <select
          value={props.context.cellType ?? ""}
          onChange={(e) =>
            props.onContextChange({ ...props.context, cellType: e.currentTarget.value || undefined })
          }
          disabled={!props.context.tissue}
          className="flex h-9 w-full rounded-md border border-input bg-background px-2 text-sm disabled:opacity-50"
        >
          <option value="">Any cell type</option>
          {cellTypesQ.data?.map((c) => (
            <option key={c.id} value={c.id}>
              {c.label}
            </option>
          ))}
        </select>
      </section>

      <section className="space-y-1.5">
        <Label className="flex items-center gap-1">
          Disease mode
          <Tooltip content="Adjusts prioritisation weights for the selected mode. Scoring only — not a filter.">
            <span className="text-muted-foreground">ⓘ</span>
          </Tooltip>
        </Label>
        <select
          value={props.context.diseaseMode ?? ""}
          onChange={(e) =>
            props.onContextChange({
              ...props.context,
              diseaseMode: e.currentTarget.value || undefined,
            })
          }
          className="flex h-9 w-full rounded-md border border-input bg-background px-2 text-sm"
        >
          <option value="">No mode filter</option>
          {diseaseModesQ.data?.map((m) => (
            <option key={m.id} value={m.id}>
              {m.label}
            </option>
          ))}
        </select>
      </section>

      <section className="space-y-1.5">
        <Label>Search partner</Label>
        <div className="relative">
          <SearchIcon className="pointer-events-none absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
          <Input
            className="h-9 pl-7 text-xs"
            placeholder="Gene symbol..."
            value={props.filters.partnerGeneSearch}
            onChange={(e) => updateFilters({ partnerGeneSearch: e.currentTarget.value })}
          />
        </div>
      </section>

      <section className="space-y-2">
        <div className="flex items-center justify-between">
          <Label>Min confidence</Label>
          <span className="font-mono text-[11px] tabular-nums text-muted-foreground">
            {Math.round(props.filters.confidenceMin * 1000)}/1000
          </span>
        </div>
        <input
          type="range"
          min={0}
          max={1}
          step={0.01}
          value={props.filters.confidenceMin}
          onChange={(e) => updateFilters({ confidenceMin: Number(e.currentTarget.value) })}
          className="w-full accent-accent"
        />
        <div className="flex justify-between text-[10px] uppercase tracking-wide text-muted-foreground">
          <span>0 Low</span>
          <span>400</span>
          <span>High 700</span>
        </div>
      </section>

      <section className="space-y-2">
        <Label>Evidence tier</Label>
        <div className="flex flex-col gap-1">
          {EVIDENCE_TIER_ORDER.slice(0, 6).map((tier) => {
            const meta = EVIDENCE_TIER_META[tier];
            const active = props.filters.evidenceTiers.includes(tier);
            return (
              <label key={tier} className="flex items-center gap-2 text-xs">
                <input
                  type="checkbox"
                  checked={active}
                  onChange={() => toggleTier(tier)}
                  className="h-3.5 w-3.5 accent-accent"
                />
                <Tooltip content={meta.description + (meta.caveat ? ` — ${meta.caveat}` : "")}>
                  <span className="inline-flex items-center gap-1.5">
                    <span
                      className="inline-block h-2 w-2 rounded-sm"
                      style={{ backgroundColor: meta.color }}
                    />
                    {meta.label}
                  </span>
                </Tooltip>
              </label>
            );
          })}
        </div>
      </section>

      <section className="mt-auto space-y-2 pt-3">
        <Button
          variant="outline"
          className="w-full justify-start"
          onClick={props.onExportCsv}
          disabled={props.exportBusy === "csv"}
        >
          {props.exportBusy === "csv" ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <FileSpreadsheet className="h-4 w-4" />
          )}
          Export CSV
        </Button>
        <Button
          variant="outline"
          className="w-full justify-start"
          onClick={props.onExportJson}
          disabled={props.exportBusy === "json"}
        >
          {props.exportBusy === "json" ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <FileJson className="h-4 w-4" />
          )}
          Export JSON
        </Button>
        <Tooltip
          content={
            props.sourceGenes.length < 2
              ? "Compare needs at least 2 query genes — add another gene to your panel first."
              : "Pivot the panel into a Shared / Unique / Hubs / Bridges view."
          }
        >
          <span className="block w-full">
            <Button
              variant="outline"
              className="w-full justify-start"
              onClick={props.onCompare}
              disabled={props.sourceGenes.length < 2}
            >
              <GitCompare className="h-4 w-4" />
              Compare genes
            </Button>
          </span>
        </Tooltip>
        <Button variant="outline" className="w-full justify-start" onClick={props.onSavePanel}>
          <Save className="h-4 w-4" />
          Save Panel
        </Button>
        <button
          type="button"
          onClick={props.onRefresh}
          disabled={props.refreshBusy}
          className="inline-flex w-full items-center justify-center gap-1.5 rounded-md px-3 py-1.5 text-xs text-muted-foreground transition-colors hover:bg-muted hover:text-foreground disabled:opacity-60"
        >
          {props.refreshBusy ? (
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
          ) : (
            <RefreshCw className="h-3.5 w-3.5" />
          )}
          Refresh data
        </button>
      </section>
    </aside>
  );
}

/** Minor helper so the parent can show an "active filters" strip above the table. */
export function ActiveFilterChips({
  filters,
  onChange,
}: {
  filters: FilterState;
  onChange: (next: FilterState) => void;
}) {
  const active = [
    ...(filters.experimentalOnly ? [{ key: "experimental", label: "Experimental only" }] : []),
    ...(filters.highPriorityOnly ? [{ key: "high", label: "High priority" }] : []),
    ...filters.evidenceTiers.map((t) => ({ key: t, label: EVIDENCE_TIER_META[t].label })),
  ];
  if (!active.length && !filters.partnerGeneSearch) return null;
  return (
    <div className="flex flex-wrap items-center gap-1.5 text-xs">
      {filters.partnerGeneSearch && (
        <span className="chip chip--active">
          partner contains "{filters.partnerGeneSearch}"
          <button type="button" onClick={() => onChange({ ...filters, partnerGeneSearch: "" })}>
            <X className="h-3 w-3" />
          </button>
        </span>
      )}
      {active.map((a) => (
        <span key={a.key} className="chip chip--active">
          {a.label}
        </span>
      ))}
      <button
        type="button"
        onClick={() =>
          onChange({
            ...filters,
            experimentalOnly: false,
            highPriorityOnly: false,
            evidenceTiers: [],
            partnerGeneSearch: "",
          })
        }
        className="chip chip--ghost text-[11px]"
      >
        Clear
      </button>
    </div>
  );
}

/** Simple helper accepted by the parent to wire the Download icon. */
export const DownloadIcon = Download;
